import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { HardwareProvider } from "./context/HardwareContext";
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Kasir from "./pages/Kasir";
import Products from "./pages/Products";
import Categories from "./pages/Categories";
import Transactions from "./pages/Transactions";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import Register from "./pages/Register";
import Live from "./pages/Live";
import Backup from "./pages/Backup";
import Customers from "./pages/Customers";
import Promotions from "./pages/Promotions";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import { homePath } from "./lib/brand";
import Settings from "./pages/Settings";
import Shift from "./pages/Shift";
import DownloadDesktop from "./pages/DownloadDesktop";
import SuperAdmin from "./pages/SuperAdmin";
import { Loader2 } from "lucide-react";

const SuperAdminGate = () => {
  const { user } = useAuth();
  return user?.is_superadmin ? <SuperAdmin /> : <Navigate to="/" replace />;
};

const Protected = ({ children, perm }) => {
  const { user, can } = useAuth();
  if (user === null)
    return (
      <div className="h-screen grid place-items-center" data-testid="auth-loading">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  const perms = perm ? [].concat(perm) : [];
  if (perms.length && !perms.some(can)) return <Navigate to={homePath(user)} replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  return <Navigate to={homePath(user)} replace />;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/daftar" element={<Register />} />
            <Route path="/lupa-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unduh" element={<DownloadDesktop />} />
            <Route
              element={
                <Protected>
                  <HardwareProvider>
                    <AppLayout />
                  </HardwareProvider>
                </Protected>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/kasir" element={<Protected perm="pos"><Kasir /></Protected>} />
              <Route path="/transaksi" element={<Transactions />} />
              <Route path="/shift" element={<Shift />} />
              <Route path="/pengaturan" element={<Settings />} />
              <Route path="/dashboard" element={<Protected perm="reports"><Dashboard /></Protected>} />
              <Route path="/live" element={<Protected perm="reports"><Live /></Protected>} />
              <Route path="/produk" element={<Protected perm={["products", "labels"]}><Products /></Protected>} />
              <Route path="/kategori" element={<Protected perm="categories"><Categories /></Protected>} />
              <Route path="/laporan" element={<Protected perm="reports"><Reports /></Protected>} />
              <Route path="/pengguna" element={<Protected perm="users"><Team /></Protected>} />
              <Route path="/backup" element={<Protected perm="backup"><Backup /></Protected>} />
              <Route path="/pelanggan" element={<Protected perm={["customers", "pos"]}><Customers /></Protected>} />
              <Route path="/promo" element={<Protected perm="promos"><Promotions /></Protected>} />
              <Route path="/super-admin" element={<SuperAdminGate />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
